"use client";
import React from "react";
import { Link } from "react-router-dom";
import {
  NotepadTextDashed,
  Twitter,
  Linkedin,
  Github,
  Mail,
  FrameIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { motion, useReducedMotion } from "motion/react";
import { Button } from "./button";

interface FooterLink {
  label: string;
  href: string;
  icon?: React.ComponentType<{ className?: string }>;
}

interface FooterLinkGroup {
  label: string;
  links: FooterLink[];
}

interface SocialLink {
  icon: React.ReactNode;
  href: string;
  label: string;
  iconComponent?: React.ComponentType<{ className?: string }>;
}

interface FooterProps {
  brandName?: string;
  brandDescription?: string;
  socialLinks?: SocialLink[];
  navLinks?: FooterLink[];
  footerLinkGroups?: FooterLinkGroup[];
  creatorName?: string;
  creatorUrl?: string;
  brandIcon?: React.ReactNode;
  bottomLogo?: React.ReactNode;
  className?: string;
}

type AnimatedContainerProps = {
  children?: React.ReactNode;
  delay?: number;
  className?: string;
};

function AnimatedContainer({
  delay = 0.1,
  children,
  className,
}: AnimatedContainerProps) {
  const shouldReduceMotion = useReducedMotion();

  if (shouldReduceMotion) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      initial={{ filter: "blur(4px)", translateY: -8, opacity: 0 }}
      whileInView={{ filter: "blur(0px)", translateY: 0, opacity: 1 }}
      viewport={{ once: true }}
      transition={{ delay, duration: 0.8 }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export const Footer = ({
  brandName = "YourBrand",
  brandDescription = "Your description here",
  socialLinks = [],
  navLinks = [],
  footerLinkGroups = [],
  creatorName,
  creatorUrl,
  brandIcon,
  bottomLogo,
  className,
}: FooterProps) => {
  // Convert navLinks to footerLinkGroups if footerLinkGroups is not provided
  const linkGroups = footerLinkGroups.length > 0 
    ? footerLinkGroups 
    : navLinks.length > 0 
      ? [{ label: "Links", links: navLinks }]
      : [];

  return (
    <section className={cn("relative w-full mt-0 overflow-hidden", className)}>
      <footer className="border-t border-zinc-800 bg-black mt-20 relative rounded-t-3xl">
        <div className="max-w-7xl flex flex-col justify-between mx-auto min-h-[30rem] sm:min-h-[35rem] md:min-h-[40rem] relative p-4 pt-6 pb-16 md:py-10 md:px-12">
          {/* Background gradients - Spotlight effect */}
          <div
            aria-hidden
            className="absolute inset-0 isolate z-0 contain-strict"
          >
            <div className="bg-[radial-gradient(68.54%_68.72%_at_55.02%_31.46%,rgba(255,255,255,0.06)_0,hsla(0,0%,55%,.02)_50%,rgba(255,255,255,0.01)_80%)] absolute top-0 left-0 h-80 w-[35rem] -translate-y-[21.875rem] -rotate-45 rounded-full" />
            <div className="bg-[radial-gradient(50%_50%_at_50%_50%,rgba(255,255,255,0.04)_0,rgba(255,255,255,0.01)_80%,transparent_100%)] absolute top-0 left-0 h-80 w-60 [translate:5%_-50%] -rotate-45 rounded-full" />
            <div className="bg-[radial-gradient(50%_50%_at_50%_50%,rgba(255,255,255,0.04)_0,rgba(255,255,255,0.01)_80%,transparent_100%)] absolute top-0 left-0 h-80 w-60 -translate-y-[21.875rem] -rotate-45 rounded-full" />
          </div>

          {/* Main content */}
          <div className="mt-4 md:mt-10 flex flex-col gap-4 md:gap-8 md:flex-row xl:mt-0 relative z-10">
            <AnimatedContainer className="w-full max-w-sm min-w-48 space-y-3 md:space-y-4">
              {brandIcon ? (
                <div className="w-8 h-8 flex items-center">{brandIcon}</div>
              ) : (
                <FrameIcon className="size-8 text-white" />
              )}
              <motion.p 
                className="text-zinc-400 mt-4 md:mt-8 text-sm"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                {brandDescription}
              </motion.p>
              {socialLinks.length > 0 && (
                <div className="flex gap-2">
                  {socialLinks.map((link, index) => (
                    <a
                      key={index}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button
                        size="icon"
                        variant="outline"
                        className="size-8 border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-600"
                      >
                        <div className="w-4 h-4">{link.icon}</div>
                        <span className="sr-only">{link.label}</span>
                      </Button>
                    </a>
                  ))}
                </div>
              )}
            </AnimatedContainer>
            {/* Link groups in 2x2 grid on mobile */}
            <div className="grid grid-cols-2 gap-4 md:flex md:gap-8 w-full">
              {linkGroups.map((group, index) => (
                <AnimatedContainer
                  key={group.label}
                  delay={0.1 + index * 0.1}
                  className="w-full"
                >
                  <div className="mb-4 md:mb-0">
                    <motion.h3 
                      className="text-sm uppercase text-white font-medium"
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: 0.1 + index * 0.1 }}
                    >
                      {group.label}
                    </motion.h3>
                    <ul className="text-zinc-400 mt-3 md:mt-4 space-y-1.5 md:space-y-2 text-sm md:text-xs lg:text-sm">
                      {group.links.map((link, linkIndex) => (
                        <motion.li 
                          key={linkIndex}
                          initial={{ opacity: 0, x: -10 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.4, delay: 0.2 + linkIndex * 0.05 }}
                          whileHover={{ x: 5 }}
                        >
                          <Link
                            to={link.href}
                            className="hover:text-white inline-flex items-center transition-all duration-300"
                          >
                            {link.icon && <link.icon className="me-1 size-4" />}
                            {link.label}
                          </Link>
                        </motion.li>
                      ))}
                    </ul>
                  </div>
                </AnimatedContainer>
              ))}
            </div>
          </div>

          {/* Bottom copyright */}
          <motion.div 
            className="text-zinc-400 flex flex-col items-center justify-between gap-2 border-t border-zinc-800 pt-2 text-sm md:flex-row relative z-10"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.7 }}
          >
            <motion.p
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.2 }}
            >
              © {new Date().getFullYear()} {brandName}. All rights reserved.
            </motion.p>
            {creatorName && creatorUrl && (
              <motion.p
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.2 }}
              >
                <a
                  href={creatorUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white transition-colors"
                >
                  {creatorName}
                </a>
              </motion.p>
            )}
          </motion.div>
        </div>

        {/* Large background text - FIXED */}
        <div 
          className="hidden md:block bg-gradient-to-b from-white/20 via-white/10 to-transparent bg-clip-text text-transparent leading-none absolute left-1/2 -translate-x-1/2 bottom-40 md:bottom-32 font-extrabold tracking-tighter pointer-events-none select-none text-center px-4"
          style={{
            fontSize: 'clamp(3rem, 12vw, 10rem)',
            maxWidth: '95vw'
          }}
        >
          {brandName.toUpperCase()}
        </div>

        {/* Bottom logo */}
        <div className="static md:absolute mx-auto mt-6 md:mt-0 hover:border-white duration-400 drop-shadow-[0_0px_20px_rgba(255,255,255,0.3)] md:bottom-20 backdrop-blur-sm rounded-3xl bg-black/60 md:left-1/2 border-2 border-zinc-800 flex items-center justify-center p-3 md:-translate-x-1/2 z-10">
          <div className="w-12 sm:w-16 md:w-24 h-12 sm:h-16 md:h-24 bg-gradient-to-br from-white to-white/80 rounded-2xl flex items-center justify-center shadow-lg">
            {bottomLogo || brandIcon || (
              <NotepadTextDashed className="w-8 sm:w-10 md:w-14 h-8 sm:h-10 md:h-14 text-black drop-shadow-lg" />
            )}
          </div>
        </div>

        {/* Bottom line */}
        <div className="hidden md:block absolute bottom-32 sm:bottom-34 backdrop-blur-sm h-1 bg-gradient-to-r from-transparent via-zinc-800 to-transparent w-full left-1/2 -translate-x-1/2"></div>

        {/* Bottom shadow */}
        <div className="hidden md:block bg-gradient-to-t from-black via-black/80 blur-[1em] to-black/40 absolute bottom-28 w-full h-24"></div>
      </footer>
    </section>
  );
};
